/**
 * API caching utility with stale-while-revalidate pattern
 *
 * This utility provides caching for API requests with configurable TTL
 * and implements the stale-while-revalidate pattern for better performance.
 */

const CACHE_TTL = 60000; // Cache time-to-live in milliseconds (1 minute)
const CACHE_STALE_TTL = 300000; // Stale cache TTL (5 minutes)

// Cache storage using a Map for in-memory caching
const apiCache = new Map();

/**
 * Make a cached API request with stale-while-revalidate pattern
 *
 * @param {string} key - Unique key for the cached request
 * @param {Function} requestFn - Function that returns a promise for the request
 * @param {Object} options - Caching options
 * @param {number} options.ttl - Fresh cache TTL in milliseconds
 * @param {number} options.staleTtl - Stale cache TTL in milliseconds
 * @param {boolean} options.forceRefresh - Force a fresh request
 * @returns {Promise} - Promise that resolves with the request result
 */
export const cachedRequest = async (key, requestFn, options = {}) => {
  const now = Date.now();
  const cachedData = apiCache.get(key);

  // Set default options
  const {
    ttl = CACHE_TTL,
    staleTtl = CACHE_STALE_TTL,
    forceRefresh = false,
  } = options;

  // Case 1: No cached data or force refresh requested - fetch fresh data
  if (!cachedData || forceRefresh) {
    try {
      const freshData = await requestFn();

      // Store in cache with timestamps
      apiCache.set(key, {
        data: freshData,
        timestamp: now,
        staleTimestamp: now + ttl,
        expireTimestamp: now + staleTtl,
      });

      return freshData;
    } catch (error) {
      // If we have stale data and there's an error fetching fresh data, return stale data
      if (cachedData && now < cachedData.expireTimestamp) {
        console.debug(
          `Error fetching fresh data for ${key}, using stale data`,
          error
        );
        return cachedData.data;
      }

      // No stale data available, must throw the error
      throw error;
    }
  }

  // Case 2: Cache is fresh - return cached data
  if (now < cachedData.staleTimestamp) {
    return cachedData.data;
  }

  // Case 3: Cache is stale but not expired - return stale data and refresh in background
  if (now < cachedData.expireTimestamp) {
    // Schedule background refresh
    Promise.resolve().then(async () => {
      try {
        const freshData = await requestFn();
        apiCache.set(key, {
          data: freshData,
          timestamp: Date.now(),
          staleTimestamp: Date.now() + ttl,
          expireTimestamp: Date.now() + staleTtl,
        });
        console.debug(`Background refresh of ${key} completed`);
      } catch (error) {
        console.debug(`Background refresh of ${key} failed`, error);
      }
    });

    // Return stale data immediately
    return cachedData.data;
  }

  // Case 4: Cache is expired - remove it and fetch fresh data
  apiCache.delete(key);
  const freshData = await requestFn();

  apiCache.set(key, {
    data: freshData,
    timestamp: now,
    staleTimestamp: now + ttl,
    expireTimestamp: now + staleTtl,
  });

  return freshData;
};

/**
 * Clear all or specific cache entries
 *
 * @param {string|null} keyPattern - Pattern to match keys (or null to clear all)
 */
export const clearCache = (keyPattern = null) => {
  if (!keyPattern) {
    apiCache.clear();
    return;
  }

  // Clear specific cache entries matching a pattern
  for (const key of apiCache.keys()) {
    if (key.includes(keyPattern)) {
      apiCache.delete(key);
    }
  }
};

/**
 * Get cache status for a key (for testing)
 *
 * @param {string} key - Cache key
 * @returns {Object|null} - Cache entry or null if not found
 */
export const getCacheStatus = (key) => {
  const entry = apiCache.get(key);
  if (!entry) return null;

  const now = Date.now();
  return {
    isFresh: now < entry.staleTimestamp,
    isStale: now >= entry.staleTimestamp && now < entry.expireTimestamp,
    isExpired: now >= entry.expireTimestamp,
    age: now - entry.timestamp,
  };
};

/**
 * Get the number of cached items (for testing)
 *
 * @returns {number} - Count of cached items
 */
export const getCacheSize = () => {
  return apiCache.size;
};
